import LiveApiClient from "../api/LiveApiClient";
import OfflineStore from "./OfflineStore";
import OfflineErrors from "./OfflineErrors";

/**
 * Item_Inventory_Fetcher — batch-fetches inventory data and caches in IDB.
 *
 * Three-tier cache: memory Map → IDB ("offline:inventory") → API.
 *
 * Usage:
 *   const fetcher = new Item_Inventory_Fetcher(catalogId, accountCode, apiKey, onDemandConfig);
 *   const inventory = await fetcher.getForItems([101, 102, 103]);
 *   fetcher.enrichItems(items); // mutates items in-place with cached inventory
 */
export default class Item_Inventory_Fetcher {
	#catalogId;
	#accountCode;
	#apiKey;
	#endpoint;
	#batchSize;
	#fields;
	#memCache = new Map(); // itemId → inventoryData

	/**
	 * @param {string|number} catalogId
	 * @param {string} accountCode
	 * @param {string} apiKey
	 * @param {object} [config] - from on_demand.inventory: { endpoint, batch_size, fields }
	 */
	constructor(catalogId, accountCode, apiKey, config = {}) {
		this.#catalogId = catalogId;
		this.#accountCode = accountCode;
		this.#apiKey = apiKey;
		this.#endpoint = (config.endpoint || "/Inventory").replace(/^\/api\//, '/');
		this.#batchSize = config.batch_size || 100;
		this.#fields = config.fields || [
			"availability", "availability_(days)"
		];
	}

	get catalogId() { return this.#catalogId; }

	/**
	 * Get inventory for a batch of item IDs.
	 * Returns from cache when possible, fetches missing from API.
	 * @param {number[]} itemIds
	 * @returns {Promise<Object>} { itemId: { availability, ... }, ... }
	 */
	async getForItems(itemIds) {
		if (!itemIds?.length) return {};

		const result = {};
		const uncachedIds = [];

		// 1. Check memory cache
		for (const id of itemIds) {
			const cached = this.#memCache.get(id);
			if (cached) {
				result[id] = cached;
			} else {
				uncachedIds.push(id);
			}
		}

		if (uncachedIds.length === 0) return result;

		// 2. Check IDB cache
		const store = OfflineStore.ForCatalog(this.#catalogId);
		const idbData = await store.GetCachedOnDemandData("inventory");
		const idbMissIds = [];

		for (const id of uncachedIds) {
			const cached = idbData?.[id];
			if (cached) {
				result[id] = cached;
				this.#memCache.set(id, cached);
			} else {
				idbMissIds.push(id);
			}
		}

		if (idbMissIds.length === 0) return result;

		// 3. Fetch from API in batches (one call per field per batch)
		const newEntries = {};
		for (let i = 0; i < idbMissIds.length; i += this.#batchSize) {
			const batch = idbMissIds.slice(i, i + this.#batchSize);
			for (const field of this.#fields) {
				try {
					const response = await LiveApiClient.get(this.#endpoint, {
						headers: {
							account: this.#accountCode,
							'api-key': this.#apiKey,
						},
						params: {
							field,
							filters: JSON.stringify([
								{ property: 'item', value: batch }
							]),
						},
					});

					if (response.data?.length) {
						for (const record of response.data) {
							if (!record.item) continue;
							const itemId = record.item;
							if (!result[itemId]) result[itemId] = {};
							if (!newEntries[itemId]) newEntries[itemId] = {};
							if (record.value !== undefined) {
								result[itemId][field] = record.value;
								newEntries[itemId][field] = record.value;
							}
						}
					}
				} catch (e) {
					console.warn(`[Item_Inventory_Fetcher] Batch fetch failed for field "${field}":`, e);
					OfflineErrors.log("Item_Inventory_Fetcher.getForItems", e);
				}
			}
		}

		// Update memory cache with merged results
		for (const [itemId, data] of Object.entries(newEntries)) {
			const numId = Number(itemId);
			const existing = this.#memCache.get(numId) || {};
			this.#memCache.set(numId, { ...existing, ...data });
		}

		// 4. Persist new entries to IDB (merge, don't replace)
		if (Object.keys(newEntries).length > 0) {
			store.MergeOnDemandData("inventory", newEntries);
		}

		return result;
	}

	/**
	 * Populate memory cache from IDB for a set of item IDs.
	 * Does not make network calls. Use before enrichItems() for offline use.
	 * @param {number[]} itemIds
	 */
	async preloadFromCache(itemIds) {
		if (!itemIds?.length) return;

		const store = OfflineStore.ForCatalog(this.#catalogId);
		const idbData = await store.GetCachedOnDemandData("inventory");
		if (!idbData) return;

		for (const id of itemIds) {
			if (!this.#memCache.has(id) && idbData[id]) {
				this.#memCache.set(id, idbData[id]);
			}
		}
	}

	/**
	 * Enrich item with cached inventory data. Mutates in-place.
	 * Only sets fields that are undefined/null on the item.
	 * @param {object} item
	 */
	enrichItem(item) {
		if (!item?.id) return;
		const cached = this.#memCache.get(item.id);
		if (!cached) return;

		for (const [key, value] of Object.entries(cached)) {
			if (item[key] === undefined || item[key] === null) {
				item[key] = value;
			}
		}
	}

	/**
	 * Enrich multiple items with cached inventory data.
	 * @param {object[]} items
	 */
	enrichItems(items) {
		if (!items?.length) return;
		for (const item of items) {
			this.enrichItem(item);
		}
	}

	/** Clear memory cache */
	clearMemoryCache() {
		this.#memCache.clear();
	}
}
